import React, { Fragment } from 'react';
import { renderRoutes } from 'react-router-config';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { graphql } from 'react-apollo';
import { connect } from 'react-redux';
import sessionStorageStore from 'store/storages/sessionStorage';
import compose from 'lodash.flowright';

import WebGalleryHeaderContainer from '../../containers/WebGalleryHeaderContainer';
import { AlbumForWebGalleries } from '../../graphql/queries/album';
import useDismissiblePersistent from '../../../../hooks/useDismissiblePersistent';
import { WG_BOTTOM_APP_DOWNLOAD_BANNER } from '../../../../constants/dismissibleKeys';
import { shouldDisplayAppDownloadBanner } from '../../../../components/global/AppDownloadBanner/helpers';
import { isMemoryBook, isVideoAlbum } from '../../../../util/album';
import WebGalleryVideoAlbum from '../../components/WebGalleryAlbum/components/WebGalleryVideoAlbum';
import WebGalleryMemoryBookAlbum from '../../components/WebGalleryAlbum/components/WebGalleryMemoryBookAlbum';
import { MeQuery } from '../../../../graphql/queries/account';
import AppDownloadBanner from '../../../../components/global/AppDownloadBanner';
import WebGalleryAlbumNavigationlessHeader from './components/WebGalleryAlbumNavigationlessHeader';

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    is_web_gallery: PropTypes.bool,
    is_private_web_gallery: PropTypes.bool,
    root_album_folder: PropTypes.shape({
      id: PropTypes.string,
      join_code: PropTypes.string,
    }),
    creator: PropTypes.shape({
      slug: PropTypes.string,
    }),
    display_public_photo_attribution: PropTypes.bool.isRequired,
  }),
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  loading: PropTypes.bool,
  authenticated: PropTypes.bool,
  route: PropTypes.any,
};

const WebGalleriesAlbumView = ({
  album,
  loading,
  authenticated,
  user,
  route,
}) => {
  /*
   * Banner should always show on a per-session basis for unauthed users,
   * once per user for authed.
   */
  const { onDismiss, visible } = useDismissiblePersistent(
    WG_BOTTOM_APP_DOWNLOAD_BANNER,
    authenticated ? undefined : sessionStorageStore,
  );
  const shouldDisplayAppDownloadBannerBool = shouldDisplayAppDownloadBanner(
    album?.root_album_folder,
  );

  const isVideoAlbumBool = isVideoAlbum(album);

  const isMemoryBookBool = isMemoryBook(album);

  if (loading || !album) {
    return (
      <div className="d-block text-center mt-6">
        <span className="spinner" />
      </div>
    );
  }

  if (isVideoAlbumBool) {
    return <WebGalleryVideoAlbum album={album} user={user} />;
  }

  if (isMemoryBookBool) {
    return <WebGalleryMemoryBookAlbum album={album} user={user} />;
  }

  return (
    <Fragment>
      <Helmet>
        <title>Album Photos &ndash; Waldo</title>
      </Helmet>

      <WebGalleryHeaderContainer />

      <div className="flex-auto">
        <WebGalleryAlbumNavigationlessHeader album={album} />
        {renderRoutes(route.routes)}
        {album && shouldDisplayAppDownloadBannerBool && visible && (
          <AppDownloadBanner onDismiss={onDismiss} />
        )}
      </div>
    </Fragment>
  );
};

WebGalleriesAlbumView.propTypes = propTypes;

const withAlbum = graphql(AlbumForWebGalleries, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumId,
    },
  }),
  props: ({ data: { loading, album } }) => ({
    loading,
    album,
  }),
});

const withUserData = graphql(MeQuery, {
  props: ({ data }) => ({
    user: data.me,
  }),
  skip: ({ authenticated }) => !authenticated,
});

const mapStateToProps = ({ auth }) => ({
  authenticated: auth.authenticated,
});

export default compose(
  connect(mapStateToProps),
  withUserData,
  withAlbum,
)(WebGalleriesAlbumView);
